/**
 * CreateProjectDrawer — portal right-drawer hosting ProjectForm in create
 * mode, opened from the top-nav Create menu's "Project" item. Unlike the
 * Work item / Task items, project creation has no project route to
 * scope into, so it skips the picker step entirely and opens straight into
 * this drawer. Drawer anatomy per DESIGN-SPEC §2.18: scrim overlay, panel r20
 * left corners, 240ms slide+fade enter, header title 17/600 + Ø36 ghost close
 * circle, Escape + outside-mousedown dismiss. The form renders `embedded` so
 * the drawer header is the only title and fields sit directly on the drawer
 * body as inset wells. On a successful create, closes and navigates to the
 * new project's workspace.
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { ProjectForm } from './ProjectForm';
import { GhostCloseButton } from './GhostIconButton';
import type { ProjectDetail } from '@/types/workItem';

// Drawer motion (§2.18/§1.7) — 240ms slide+fade; co-located <style> because
// index.css is owned by another batch. Identical keyframes are also declared
// by TicketDetailDrawer — duplicate declarations are harmless.
const DRAWER_MOTION_CSS = `
@keyframes seedzDrawerIn { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: none; } }
@keyframes seedzScrimIn { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .seedz-drawer-panel, .seedz-drawer-scrim { animation: none !important; }
}
`;

interface CreateProjectDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function CreateProjectDrawer({ open, onClose }: CreateProjectDrawerProps) {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Escape key: close the drawer.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Click-away dismiss — same outside-mousedown idiom as TaskDrawer.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  // Null guard — render nothing while closed, same idiom as TaskDrawer/CrDrawer.
  if (!open) return null;

  function handleCreated(project?: ProjectDetail) {
    onClose();
    if (project) navigate(`/project/${project.id}`);
  }

  // Portal to <body>: the routed page wrapper's .route-enter transform would
  // otherwise trap position:fixed and clip this drawer to the page box.
  return createPortal(
    <>
      <style>{DRAWER_MOTION_CSS}</style>
      {/* Scrim (§2.18/§1.1) — dims the page so the drawer reads as the one
          floating layer; clicks land outside the panel and close via the
          document mousedown handler above. */}
      <div
        className="seedz-drawer-scrim"
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--scrim-cockpit)',
          zIndex: 199,
          animation: 'seedzScrimIn 240ms var(--ease-out) both',
        }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-label="New project"
        className="seedz-drawer-panel"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 480,
          maxWidth: 'calc(100vw - 48px)',
          background: 'var(--surface)',
          // Floating layer (§2.18): r20 left corners + soft shadow — no
          // border-left stroke; the scrim and shadow do the separation.
          borderRadius: 'var(--radius-lg) 0 0 var(--radius-lg)',
          boxShadow: 'var(--shadow-2)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 200,
          animation: 'seedzDrawerIn 240ms var(--ease-out) both',
        }}
      >
        {/* Header — drawer title 17/600 + Ø36 ghost close circle (§2.18) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            padding: '14px 20px',
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-lg)',
              fontWeight: 600,
              color: 'var(--text)',
            }}
          >
            New project
          </span>
          <GhostCloseButton onClick={onClose} />
        </div>

        {/* Body — embedded ProjectForm in create mode (no duplicate heading,
            no nested raised card; fields sit as inset wells on the drawer
            body per §2.18). No accountId prop so it renders its own required
            Account select above the other fields. */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          <ProjectForm editId={null} embedded onDone={handleCreated} onCancel={onClose} />
        </div>
      </div>
    </>,
    document.body,
  );
}
